/**
 * Turns a raw `RTCStatsReport` into something a tile can show.
 *
 * WebRTC reports counters, not rates: `packetsLost` is a running total for the
 * life of the connection. A call that dropped badly for ten seconds and then
 * recovered would keep showing that loss forever if the total were read
 * directly. Everything here is therefore a delta against the previous sample,
 * which is what makes the indicator track the last few seconds rather than the
 * whole meeting.
 */

export type QualityScore = 'good' | 'fair' | 'poor';

export interface PeerQuality {
  score: QualityScore;
  /** Share of expected packets lost since the previous sample, 0–1. */
  packetLoss: number;
  roundTripMs: number | null;
  jitterMs: number | null;
  /**
   * Media is going through a TURN relay rather than directly to the peer.
   * Worth surfacing: it is the difference between a call that works and one
   * that silently would not have, and it is the only visible sign that the
   * relay is earning its bandwidth bill.
   */
  relayed: boolean;
  /** Set when the browser is throttling *our* upload rather than theirs. */
  limitation: 'bandwidth' | 'cpu' | null;
}

/** The counters carried between polls so the next one can compute a delta. */
export interface StatsSample {
  packetsReceived: number;
  packetsLost: number;
}

export const UNKNOWN_QUALITY: PeerQuality = {
  score: 'good',
  packetLoss: 0,
  roundTripMs: null,
  jitterMs: null,
  relayed: false,
  limitation: null,
};

/**
 * Thresholds are deliberately forgiving.
 *
 * A meeting indicator that cries wolf gets ignored, and short loss spikes are
 * normal on wifi — the encoder absorbs them. These are set where a human
 * actually starts to hear chopping or see the picture break up: a few percent
 * loss is survivable, five percent is not, and round-trip time only becomes
 * conversationally awkward past about a third of a second.
 */
function scoreOf(packetLoss: number, roundTripMs: number | null): QualityScore {
  if (packetLoss >= 0.05 || (roundTripMs !== null && roundTripMs >= 400)) return 'poor';
  if (packetLoss >= 0.02 || (roundTripMs !== null && roundTripMs >= 250)) return 'fair';
  return 'good';
}

export function summarizeStats(
  report: RTCStatsReport,
  previous: StatsSample | null,
): { quality: PeerQuality; sample: StatsSample } {
  let packetsReceived = 0;
  let packetsLost = 0;
  let jitterMs: number | null = null;
  let roundTripMs: number | null = null;
  let relayed = false;
  let limitation: PeerQuality['limitation'] = null;

  report.forEach((entry: Record<string, unknown>) => {
    const type = entry.type as string;

    if (type === 'inbound-rtp' && !entry.isRemote) {
      packetsReceived += (entry.packetsReceived as number) ?? 0;
      packetsLost += (entry.packetsLost as number) ?? 0;
      // Reported in seconds. Video jitter is the one people perceive, so let
      // it win when a connection carries both.
      if (typeof entry.jitter === 'number' && (jitterMs === null || entry.kind === 'video')) {
        jitterMs = entry.jitter * 1000;
      }
    }

    if (type === 'candidate-pair' && entry.state === 'succeeded' && entry.nominated) {
      if (typeof entry.currentRoundTripTime === 'number') {
        roundTripMs = entry.currentRoundTripTime * 1000;
      }
    }

    // `relay` on either end means the media is not travelling peer to peer.
    if (type === 'local-candidate' || type === 'remote-candidate') {
      if (entry.candidateType === 'relay') relayed = true;
    }

    if (type === 'outbound-rtp' && entry.kind === 'video') {
      const reason = entry.qualityLimitationReason;
      if (reason === 'bandwidth' || reason === 'cpu') limitation = reason;
    }
  });

  const sample: StatsSample = { packetsReceived, packetsLost };

  // The first poll has no baseline, so there is no rate to report yet —
  // claiming zero loss would be a guess, but it is the harmless one.
  let packetLoss = 0;
  if (previous) {
    const received = packetsReceived - previous.packetsReceived;
    const lost = packetsLost - previous.packetsLost;
    const expected = received + lost;
    // A connection that restarted can report counters lower than last time.
    if (expected > 0 && lost >= 0) packetLoss = Math.min(1, lost / expected);
  }

  return {
    quality: {
      score: scoreOf(packetLoss, roundTripMs),
      packetLoss,
      roundTripMs,
      jitterMs,
      relayed,
      limitation,
    },
    sample,
  };
}
